import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { toast } from '../ui/use-toast';
import { apiRequest } from '../../config/api';
import { X, Check, Copy, Trash2, Download } from 'lucide-react';
import ProcedureTrackingForm from './ProcedureTrackingForm';

/* ========================
   Config & helpers
   ======================== */
const adultTeethNumbers = [8, 7, 6, 5, 4, 3, 2, 1, 1, 2, 3, 4, 5, 6, 7, 8];
const childTeethNumbers = ['E', 'D', 'C', 'B', 'A', 'A', 'B', 'C', 'D', 'E'];
const gradeOptions = ["A", "B", "C", "D", "E"];
const statusOptions = [
  "Intact",
  "Caries",
  "Filled",
  "Missing",
  "Crown",
  "Fractured",
  "Root Canal Treated",
];

const getColorClass = (grade) => {
  switch (grade) {
    case "A": return "bg-green-700/20 border-green-700 text-green-300";
    case "B": return "bg-green-200/20 border-green-200 text-green-600";
    case "C": return "bg-orange-200/20 border-orange-200 text-orange-300";
    case "D": return "bg-red-400/20 border-red-400 text-red-400";
    case "E": return "bg-red-800/30 border-red-800 text-red-300";
    default: return "bg-gray-800 border-gray-600 text-gray-400 hover:bg-gray-700";
  }
};

const Grid16 = ({ children, className = "" }) => (
  <div className={`grid gap-1.5 ${className}`} style={{ gridTemplateColumns: 'repeat(16, minmax(0, 1fr))' }}>
    {children}
  </div>
);

const Modal = ({ open, title, onClose, children }) => {
  if (!open) return null;
  
  const modalContent = (
    <>
      {/* Backdrop - separate element */}
      <div 
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100]"
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0
        }}
        onClick={onClose}
      />
      
      {/* Modal Container */}
      <div 
        className="fixed inset-0 z-[101] flex items-center justify-center p-4 pointer-events-none"
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflowY: 'auto',
          overscrollBehavior: 'contain'
        }}
      >
        {/* Modal Content - re-enable pointer events */}
        <div 
          className="relative w-full max-w-md glass-effect border-gray-700 rounded-xl shadow-xl my-auto pointer-events-auto"
          style={{ 
            maxHeight: '90vh',
            overflowY: 'auto',
            margin: 'auto',
            position: 'relative'
          }}
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700 sticky top-0 bg-gray-900/95 backdrop-blur-sm z-10">
            <h3 className="text-lg font-semibold text-white">{title}</h3>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClose();
              }}
              className="text-gray-400 hover:text-white rounded-full p-1 hover:bg-gray-700 transition-colors cursor-pointer"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-5">
            {children}
          </div>
        </div>
      </div>
    </>
  );
  
  // Use portal to render outside the DialogContent to avoid scroll issues
  return typeof document !== 'undefined' 
    ? createPortal(modalContent, document.body)
    : modalContent;
};

const MAX_NOTES = 600;

const DentalExaminationForm = (props) => {
  // Destructure props with defaults
  const {
    hideDialog = false,
    open,
    onClose,
    patientId,
    patientName,
    onBack,
    onComplete
  } = props || {};
  
  // Ensure hideDialog is a boolean
  const isHideDialog = hideDialog === true;
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [showProcedureTracking, setShowProcedureTracking] = useState(false);
  const [medicalHistoryData, setMedicalHistoryData] = useState(null);
  const [dentalExaminationData, setDentalExaminationData] = useState(null);
  
  // Patient type (Adult/Child)
  const [patientType, setPatientType] = useState("Adult");

  // Chief Complaint (default: "General Cleaning")
  const [chiefComplaint, setChiefComplaint] = useState("General Cleaning");
  const [durationOnset, setDurationOnset] = useState("");
  const [triggerFactors, setTriggerFactors] = useState([]);
  const [otherTrigger, setOtherTrigger] = useState("");

  // Diagnosis & plan
  const [diagnosisNotes, setDiagnosisNotes] = useState("");
  const [treatmentPlanNotes, setTreatmentPlanNotes] = useState("");

  // Grid state (grade + status per tooth)
  // Adult: 16 teeth, Child: 10 teeth
  const [upperGrades, setUpperGrades] = useState(Array(16).fill(""));
  const [lowerGrades, setLowerGrades] = useState(Array(16).fill(""));
  const [upperStatus, setUpperStatus] = useState(Array(16).fill(""));
  const [lowerStatus, setLowerStatus] = useState(Array(16).fill(""));

  // Selection modal
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  // Validation
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});

  // Keyboard nav (will be adjusted based on patient type)
  const gridRefsUpper = useRef(Array.from({ length: 16 }, () => React.createRef()));
  const gridRefsLower = useRef(Array.from({ length: 16 }, () => React.createRef()));

  // Get current tooth numbers and grid size based on patient type
  const teethNumbers = patientType === "Child" ? childTeethNumbers : adultTeethNumbers;
  const gridSize = patientType === "Child" ? 10 : 16;

  // Load existing data when form opens (only if patientId exists)
  useEffect(() => {
    if (open && patientId && !showProcedureTracking) {
      console.log('DentalExaminationForm: Loading data for patientId:', patientId);
      fetchAllData();
    } else if (!open && !showProcedureTracking) {
      // Only reset form when dialog closes and procedure tracking is not open
      resetForm();
    }
  }, [open, patientId, showProcedureTracking]);

  // Fetch both medical history and dental examination
  const fetchAllData = async () => {
    if (!patientId) {
      console.warn('DentalExaminationForm: Cannot fetch - patientId is missing');
      return;
    }
    try {
      // Fetch medical history
      try {
        const mhData = await apiRequest(`get_medical_history.php?patient_id=${patientId}`);
        if (mhData.success && mhData.history) {
          setMedicalHistoryData(mhData.history);
        }
      } catch (error) {
        console.error('Error fetching medical history:', error);
      }

      // Fetch dental examination
      console.log('DentalExaminationForm: Fetching data for patientId:', patientId);
      const data = await apiRequest(`get_dental_examination.php?patient_id=${patientId}`);
      
      // Handle case where examination is null (no data yet)
      if (data.success && data.examination === null) {
        console.log('DentalExaminationForm: No existing examination found, resetting to defaults');
        resetForm();
        return;
      }
      
      if (data.success && data.examination) {
        const exam = data.examination;
        
        // Set patient type (default to Adult if not set)
        setPatientType(exam.patient_type || "Adult");
        
        // Set chief complaint (default to "General Cleaning" if empty or whitespace)
        const complaintValue = exam.chief_complaint && exam.chief_complaint.trim() 
          ? exam.chief_complaint 
          : "General Cleaning";
        setChiefComplaint(complaintValue);
        setDurationOnset(exam.duration_onset || "");
        
        // Parse trigger_factors safely
        if (exam.trigger_factors) {
          if (typeof exam.trigger_factors === 'string') {
            try {
              setTriggerFactors(JSON.parse(exam.trigger_factors));
            } catch (e) {
              console.error('Error parsing trigger_factors:', e);
              setTriggerFactors([]);
            }
          } else if (Array.isArray(exam.trigger_factors)) {
            setTriggerFactors(exam.trigger_factors);
          } else {
            setTriggerFactors([]);
          }
        } else {
          setTriggerFactors([]);
        }
        
        setOtherTrigger(exam.other_trigger || "");
        setDiagnosisNotes(exam.diagnosis_notes || "");
        setTreatmentPlanNotes(exam.treatment_plan_notes || "");
        
        // Parse findings - ensure they're properly loaded
        if (exam.findings) {
          let findings;
          if (typeof exam.findings === 'string') {
            try {
              findings = JSON.parse(exam.findings);
            } catch (e) {
              console.error('Error parsing findings JSON:', e);
              findings = { upper: [], lower: [] };
            }
          } else {
            findings = exam.findings;
          }
          
          const isChild = (exam.patient_type || "Adult") === "Child";
          const size = isChild ? 10 : 16;
          
          console.log('Loading findings:', { findings, isChild, size });
          
          if (findings.upper && Array.isArray(findings.upper)) {
            const upperG = findings.upper.map(t => (typeof t === 'object' ? t.grade : "") || "");
            const upperS = findings.upper.map(t => (typeof t === 'object' ? t.status : "") || "");
            // Pad or truncate to current size
            const paddedUpperG = [...upperG.slice(0, size), ...Array(Math.max(0, size - upperG.length)).fill("")];
            const paddedUpperS = [...upperS.slice(0, size), ...Array(Math.max(0, size - upperS.length)).fill("")];
            setUpperGrades(paddedUpperG);
            setUpperStatus(paddedUpperS);
            console.log('Loaded upper findings:', { upperG, upperS, paddedUpperG, paddedUpperS });
          } else {
            // Initialize empty arrays if no upper findings
            setUpperGrades(Array(size).fill(""));
            setUpperStatus(Array(size).fill(""));
          }
          
          if (findings.lower && Array.isArray(findings.lower)) {
            const lowerG = findings.lower.map(t => (typeof t === 'object' ? t.grade : "") || "");
            const lowerS = findings.lower.map(t => (typeof t === 'object' ? t.status : "") || "");
            const paddedLowerG = [...lowerG.slice(0, size), ...Array(Math.max(0, size - lowerG.length)).fill("")];
            const paddedLowerS = [...lowerS.slice(0, size), ...Array(Math.max(0, size - lowerS.length)).fill("")];
            setLowerGrades(paddedLowerG);
            setLowerStatus(paddedLowerS);
            console.log('Loaded lower findings:', { lowerG, lowerS, paddedLowerG, paddedLowerS });
          } else {
            // Initialize empty arrays if no lower findings
            setLowerGrades(Array(size).fill(""));
            setLowerStatus(Array(size).fill(""));
          }
        } else {
          // No findings - initialize empty arrays based on patient type
          const isChild = (exam.patient_type || "Adult") === "Child";
          const size = isChild ? 10 : 16;
          setUpperGrades(Array(size).fill(""));
          setLowerGrades(Array(size).fill(""));
          setUpperStatus(Array(size).fill(""));
          setLowerStatus(Array(size).fill(""));
        }
      } else {
        // No existing examination found - reset to defaults
        console.log('DentalExaminationForm: No examination data found, resetting to defaults');
        resetForm();
      }
      // Store dental examination data for Review form
      if (data.success && data.examination) {
        setDentalExaminationData(data.examination);
      }
      
      setErrors({});
    } catch (error) {
      console.error('Error fetching dental examination:', error);
      // If fetch fails, reset to defaults
      resetForm();
      
      // Show user-friendly error message
      if (error.message && error.message.includes('JSON')) {
        toast({
          title: 'Warning',
          description: 'Could not load previous examination data. Starting fresh.',
          variant: 'default'
        });
      }
    }
  };

  const resetForm = () => {
    setPatientType("Adult");
    setChiefComplaint("General Cleaning");
    setDurationOnset("");
    setTriggerFactors([]);
    setOtherTrigger("");
    setDiagnosisNotes("");
    setTreatmentPlanNotes("");
    setUpperGrades(Array(16).fill(""));
    setLowerGrades(Array(16).fill(""));
    setUpperStatus(Array(16).fill(""));
    setLowerStatus(Array(16).fill(""));
    setErrors({});
    setTouched({});
  };

  // Handle patient type change
  const handlePatientTypeChange = (type) => {
    setPatientType(type);
    const newSize = type === "Child" ? 10 : 16;
    const currentSize = patientType === "Child" ? 10 : 16;
    
    if (newSize !== currentSize) {
      // Resize arrays when switching between adult and child
      if (newSize < currentSize) {
        // Switching to child - truncate
        setUpperGrades(upperGrades.slice(0, newSize));
        setLowerGrades(lowerGrades.slice(0, newSize));
        setUpperStatus(upperStatus.slice(0, newSize));
        setLowerStatus(lowerStatus.slice(0, newSize));
      } else {
        // Switching to adult - pad with empty strings
        setUpperGrades([...upperGrades, ...Array(newSize - currentSize).fill("")]);
        setLowerGrades([...lowerGrades, ...Array(newSize - currentSize).fill("")]);
        setUpperStatus([...upperStatus, ...Array(newSize - currentSize).fill("")]);
        setLowerStatus([...lowerStatus, ...Array(newSize - currentSize).fill("")]);
      }
    }
  };

  const openPicker = (jaw, index) => {
    setSelected({ jaw, index });
    setPickerOpen(true);
  };

  const applySelection = (grade, status) => {
    if (!selected) return;
    const { jaw, index } = selected;
    if (jaw === "upper") {
      const g = [...upperGrades]; g[index] = grade; setUpperGrades(g);
      const s = [...upperStatus]; s[index] = status; setUpperStatus(s);
    } else {
      const g = [...lowerGrades]; g[index] = grade; setLowerGrades(g);
      const s = [...lowerStatus]; s[index] = status; setLowerStatus(s);
    }
    setPickerOpen(false);
  };

  const toggleTrigger = (factor) => {
    setTriggerFactors((prev) =>
      prev.includes(factor) ? prev.filter((f) => f !== factor) : [...prev, factor]
    );
  };

  // Build payload
  const payload = useMemo(() => {
    const currentTeethNumbers = patientType === "Child" ? childTeethNumbers : adultTeethNumbers;
    const currentSize = patientType === "Child" ? 10 : 16;
    
    const upper = currentTeethNumbers.map((num, idx) => ({
      tooth: num,
      grade: upperGrades[idx] || "",
      status: upperStatus[idx] || "",
    })).slice(0, currentSize);
    const lower = currentTeethNumbers.map((num, idx) => ({
      tooth: num,
      grade: lowerGrades[idx] || "",
      status: lowerStatus[idx] || "",
    })).slice(0, currentSize);
    const triggers =
      otherTrigger && triggerFactors.includes("Other")
        ? [...triggerFactors.filter((t) => t !== "Other"), `Other: ${otherTrigger}`]
        : triggerFactors;

    return {
      patient_type: patientType,
      chief_complaint: chiefComplaint.trim(),
      duration_onset: durationOnset.trim(),
      trigger_factors: triggers,
      other_trigger: triggerFactors.includes("Other") ? otherTrigger.trim() : "",
      findings: { upper, lower },
      diagnosis_notes: diagnosisNotes.trim(),
      treatment_plan_notes: treatmentPlanNotes.trim(),
    };
  }, [
    patientType,
    chiefComplaint,
    durationOnset,
    triggerFactors,
    otherTrigger,
    upperGrades,
    lowerGrades,
    upperStatus,
    lowerStatus,
    diagnosisNotes,
    treatmentPlanNotes,
  ]);

  // Validation
  const validate = (data = payload) => {
    const e = {};
    if (!data.chief_complaint) e.chiefComplaint = "Please enter the chief complaint";
    if (triggerFactors.includes("Other") && !otherTrigger.trim()) e.otherTrigger = "Please specify other trigger";
    const tooLong = (s) => s && s.length > MAX_NOTES;
    if (tooLong(data.diagnosis_notes)) e.diagnosisNotes = `Keep under ${MAX_NOTES} characters`;
    if (tooLong(data.treatment_plan_notes)) e.treatmentPlanNotes = `Keep under ${MAX_NOTES} characters`;
    return e;
  };

  useEffect(() => {
    setErrors(validate());
  }, [payload, triggerFactors, otherTrigger]);

  const isFormValid = Object.keys(validate()).length === 0;
  const setFieldTouched = (name) => setTouched((t) => ({ ...t, [name]: true }));
  const count = (s) => (s ? s.length : 0);

  const handleSaveDraft = async () => {
    // Validate patient ID
    if (!patientId) {
      toast({
        title: 'Error',
        description: 'Patient ID is missing. Please close and reopen the form.',
        variant: 'destructive'
      });
      return;
    }

    // Ensure patientId is a valid number
    const validPatientId = parseInt(patientId, 10);
    if (isNaN(validPatientId) || validPatientId <= 0) {
      toast({
        title: 'Error',
        description: 'Invalid Patient ID. Please close and reopen the form.',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      const requestBody = {
        patient_id: validPatientId,
        ...payload,
        is_draft: true
      };

      console.log('Saving draft with payload:', {
        patient_id: validPatientId,
        findings: payload.findings,
        upperGrades: upperGrades,
        lowerGrades: lowerGrades,
        upperStatus: upperStatus,
        lowerStatus: lowerStatus
      });

      const data = await apiRequest('add_dental_examination.php', {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      if (data.success) {
        toast({
          title: 'Draft Saved',
          description: 'Your progress has been saved. You can continue later.',
          variant: 'default'
        });
      } else {
        toast({
          title: 'Error',
          description: data.error || 'Failed to save draft',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save draft',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    // Validate patient ID
    if (!patientId) {
      toast({
        title: 'Error',
        description: 'Patient ID is missing. Please close and reopen the form.',
        variant: 'destructive'
      });
      return;
    }

    const emap = validate();
    setErrors(emap);
    setTouched({
      chiefComplaint: true,
      otherTrigger: true,
      diagnosisNotes: true,
      treatmentPlanNotes: true,
    });
    if (Object.keys(emap).length) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      // Ensure patientId is a valid number
      const validPatientId = parseInt(patientId, 10);
      if (isNaN(validPatientId) || validPatientId <= 0) {
        toast({
          title: 'Error',
          description: 'Invalid Patient ID. Please close and reopen the form.',
          variant: 'destructive'
        });
        setLoading(false);
        return;
      }

      const requestBody = {
        patient_id: validPatientId,
        ...payload,
        is_draft: false
      };

      console.log('Submitting dental examination:', { 
        patientId: validPatientId, 
        payload,
        findings: payload.findings,
        upperGrades: upperGrades,
        lowerGrades: lowerGrades,
        upperStatus: upperStatus,
        lowerStatus: lowerStatus
      });

      const data = await apiRequest('add_dental_examination.php', {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      if (data.success) {
        toast({
          title: 'Success',
          description: 'Dental examination saved successfully',
          variant: 'default'
        });
        
        // Store current dental examination data
        const examData = {
          ...payload,
          patient_type: patientType,
          chief_complaint: chiefComplaint,
          duration_onset: durationOnset,
          trigger_factors: payload.trigger_factors,
          other_trigger: otherTrigger,
          findings: payload.findings,
          diagnosis_notes: diagnosisNotes,
          treatment_plan_notes: treatmentPlanNotes
        };
        setDentalExaminationData(examData);
        
        // If hideDialog is true, we're in a wizard - call onComplete with data
        if (isHideDialog && onComplete) {
          onComplete(examData);
        } else {
          // Open procedure tracking form without closing dental examination form
          setShowProcedureTracking(true);
        }
      } else {
        toast({
          title: 'Error',
          description: data.error || 'Failed to save dental examination',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save dental examination',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const clearGrid = () => {
    const size = patientType === "Child" ? 10 : 16;
    setUpperGrades(Array(size).fill(""));
    setLowerGrades(Array(size).fill(""));
    setUpperStatus(Array(size).fill(""));
    setLowerStatus(Array(size).fill(""));
  };

  const copyUpperToLower = () => {
    setLowerGrades([...upperGrades]);
    setLowerStatus([...upperStatus]);
  };

  // Helpers to read currently selected tooth's values
  const currentGrade =
    selected?.jaw === "upper"
      ? upperGrades[selected.index]
      : selected?.jaw === "lower"
      ? lowerGrades[selected.index]
      : "";
  const currentStatus =
    selected?.jaw === "upper"
      ? upperStatus[selected.index]
      : selected?.jaw === "lower"
      ? lowerStatus[selected.index]
      : "";

  const toothLabel = selected
    ? `${selected.jaw === "upper" ? "Upper" : "Lower"} — ${teethNumbers[selected.index] || ""}`
    : "";

  // Keyboard navigation for cells
  const handleKey = (e, jaw, idx) => {
    const isUpper = jaw === "upper";
    const refs = isUpper ? gridRefsUpper.current : gridRefsLower.current;
    const otherRefs = isUpper ? gridRefsLower.current : gridRefsUpper.current;

    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openPicker(jaw, idx);
      return;
    }
    if (e.key === "ArrowRight") {
      e.preventDefault();
      (refs[idx + 1] || refs[idx])?.current?.focus();
      return;
    }
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      (refs[idx - 1] || refs[idx])?.current?.focus();
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      (otherRefs[idx] || otherRefs[0])?.current?.focus();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      (otherRefs[idx] || otherRefs[0])?.current?.focus();
      return;
    }
  };

  // Quick summary of marked findings (only count visible teeth)
  const markedCount = useMemo(() => {
    const currentSize = patientType === "Child" ? 10 : 16;
    const visibleUpperGrades = upperGrades.slice(0, currentSize);
    const visibleLowerGrades = lowerGrades.slice(0, currentSize);
    const visibleUpperStatus = upperStatus.slice(0, currentSize);
    const visibleLowerStatus = lowerStatus.slice(0, currentSize);
    
    const g = [...visibleUpperGrades, ...visibleLowerGrades].filter(Boolean).length;
    const s = [...visibleUpperStatus, ...visibleLowerStatus].filter(Boolean).length;
    return { g, s };
  }, [patientType, upperGrades, lowerGrades, upperStatus, lowerStatus]);

  const formContent = (
    <>
    <form onSubmit={handleSubmit} className="space-y-6">
          {/* Chief Complaint */}
          <div className="space-y-2">
            <Label htmlFor="chief" className="text-white text-sm font-medium">
              Patient's Chief Complaint <span className="text-red-400">*</span>
            </Label>
            <Textarea
              id="chief"
              className={`w-full rounded-lg border px-4 py-3 text-sm bg-gray-800 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                touched.chiefComplaint && errors.chiefComplaint ? "border-red-500" : "border-gray-600"
              }`}
              value={chiefComplaint}
              onChange={(e) => setChiefComplaint(e.target.value.slice(0, MAX_NOTES))}
              onBlur={() => setFieldTouched("chiefComplaint")}
              placeholder="General Cleaning"
              rows={3}
            />
            <p className="text-xs text-gray-400">{count(chiefComplaint)}/{MAX_NOTES}</p>
            {touched.chiefComplaint && errors.chiefComplaint && (
              <p className="text-xs text-red-400">{errors.chiefComplaint}</p>
            )}
          </div>

          {/* Duration & onset */}
          <div className="space-y-2">
            <Label htmlFor="duration" className="text-white text-sm font-medium">
              Duration & Onset
            </Label>
            <Input
              id="duration"
              className="w-full rounded-lg border border-gray-600 bg-gray-800 text-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={durationOnset}
              onChange={(e) => setDurationOnset(e.target.value)}
              placeholder="e.g., 2 weeks, gradual onset"
            />
          </div>

          {/* Trigger factors */}
          <div className="space-y-2">
            <Label className="text-white text-sm font-medium">Trigger factors (select all that apply)</Label>
            <div className="flex flex-wrap gap-2">
              {["Hot", "Cold", "Sweets", "Biting", "Chewing", "Other"].map((factor) => {
                const active = triggerFactors.includes(factor);
                return (
                  <button
                    type="button"
                    key={factor}
                    onClick={() => toggleTrigger(factor)}
                    className={`px-4 py-2 rounded-full border text-sm font-medium transition-all flex items-center ${
                      active
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-gray-800 text-gray-300 border-gray-600 hover:border-gray-500"
                    }`}
                  >
                    {active && <Check className="w-4 h-4 mr-1.5" />}
                    {factor}
                  </button>
                );
              })}
            </div>
            {triggerFactors.includes("Other") && (
              <div className="mt-3">
                <Input
                  id="otherTrigger"
                  className={`w-full rounded-lg border px-4 py-2.5 text-sm bg-gray-800 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    touched.otherTrigger && errors.otherTrigger ? "border-red-500" : "border-gray-600"
                  }`}
                  value={otherTrigger}
                  onChange={(e) => setOtherTrigger(e.target.value)}
                  onBlur={() => setFieldTouched("otherTrigger")}
                  placeholder="Specify other trigger"
                />
                {touched.otherTrigger && errors.otherTrigger && (
                  <p className="mt-1 text-xs text-red-400">{errors.otherTrigger}</p>
                )}
              </div>
            )}
          </div>

          {/* Teeth Findings Grid */}
          <div className="p-5 border border-gray-700 rounded-xl bg-gray-800/50">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="text-lg font-semibold text-white">Dental Findings</h3>
              <div className="flex items-center gap-2 flex-wrap">
                {/* Adult/Child Toggle */}
                <div className="flex items-center gap-2 bg-gray-800 rounded-lg p-1 border border-gray-600">
                  <button
                    type="button"
                    onClick={() => handlePatientTypeChange("Adult")}
                    className={`px-3 py-1.5 text-xs font-medium rounded transition-all ${
                      patientType === "Adult"
                        ? "bg-blue-600 text-white"
                        : "text-gray-400 hover:text-white"
                    }`}
                  >
                    Adult
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePatientTypeChange("Child")}
                    className={`px-3 py-1.5 text-xs font-medium rounded transition-all ${
                      patientType === "Child"
                        ? "bg-blue-600 text-white"
                        : "text-gray-400 hover:text-white"
                    }`}
                  >
                    Child
                  </button>
                </div>
                <span className="text-xs text-gray-400 bg-gray-800 px-2 py-1 rounded">
                  Tip: Use ← → ↑ ↓ to move, Enter to edit
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={copyUpperToLower}
                  className="text-xs border-gray-600 bg-gray-800 text-white hover:bg-gray-700"
                >
                  <Copy className="w-3 h-3 mr-1" />
                  Copy Upper → Lower
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={clearGrid}
                  className="text-xs border-gray-600 bg-gray-800 text-white hover:bg-gray-700"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  Clear Grid
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
              <span>Upper (Right → Left)</span>
              <span className="rounded bg-gray-800 px-2 py-0.5">
                Marked: {markedCount.g} grades / {markedCount.s} status
              </span>
            </div>

            {/* Upper Grade cells */}
            <div className="grid gap-1.5 mb-2" style={{ gridTemplateColumns: `repeat(${gridSize}, minmax(0, 1fr))` }}>
              {upperGrades.slice(0, gridSize).map((grade, idx) => {
                // Ensure ref exists
                if (!gridRefsUpper.current[idx]) {
                  gridRefsUpper.current[idx] = React.createRef();
                }
                return (
                  <button
                    key={`u-${idx}`}
                    type="button"
                    ref={gridRefsUpper.current[idx]}
                    onClick={() => openPicker("upper", idx)}
                    onKeyDown={(e) => handleKey(e, "upper", idx)}
                    className={`h-10 rounded-md border text-sm font-medium flex items-center justify-center transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 ${getColorClass(grade)} ${grade ? "shadow-sm" : ""}`}
                    title={upperStatus[idx] ? `Status: ${upperStatus[idx]}` : "Set Grade/Status"}
                    tabIndex={0}
                  >
                    {grade || "-"}
                  </button>
                );
              })}
            </div>

            {/* Upper tooth numbers */}
            <div className="grid gap-1.5 mb-5" style={{ gridTemplateColumns: `repeat(${gridSize}, minmax(0, 1fr))` }}>
              {teethNumbers.map((num, idx) => (
                <div key={`un-${idx}`} className="text-center text-sm font-medium text-gray-400 select-none">
                  {num}
                </div>
              ))}
            </div>

            <div className="text-xs text-gray-400 mb-2">Lower (Right ← Left)</div>

            {/* Lower tooth numbers */}
            <div className="grid gap-1.5 mb-2" style={{ gridTemplateColumns: `repeat(${gridSize}, minmax(0, 1fr))` }}>
              {teethNumbers.map((num, idx) => (
                <div key={`ln-${idx}`} className="text-center text-sm font-medium text-gray-400 select-none">
                  {num}
                </div>
              ))}
            </div>

            {/* Lower Grade cells */}
            <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${gridSize}, minmax(0, 1fr))` }}>
              {lowerGrades.slice(0, gridSize).map((grade, idx) => {
                // Ensure ref exists
                if (!gridRefsLower.current[idx]) {
                  gridRefsLower.current[idx] = React.createRef();
                }
                return (
                  <button
                    key={`l-${idx}`}
                    type="button"
                    ref={gridRefsLower.current[idx]}
                    onClick={() => openPicker("lower", idx)}
                    onKeyDown={(e) => handleKey(e, "lower", idx)}
                    className={`h-10 rounded-md border text-sm font-medium flex items-center justify-center transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 ${getColorClass(grade)} ${grade ? "shadow-sm" : ""}`}
                    title={lowerStatus[idx] ? `Status: ${lowerStatus[idx]}` : "Set Grade/Status"}
                    tabIndex={0}
                  >
                    {grade || "-"}
                  </button>
                );
              })}
            </div>

            {/* Legend */}
            <div className="mt-6">
              <h4 className="font-medium text-sm text-white mb-3">GRADE LEGEND</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-6 text-sm">
                <div className="flex items-center">
                  <span className="w-5 h-5 rounded bg-green-700/20 border border-green-700 mr-2 flex-shrink-0"></span>
                  <span className="text-gray-300">A — No issue</span>
                </div>
                <div className="flex items-center">
                  <span className="w-5 h-5 rounded bg-green-200/20 border border-green-200 mr-2 flex-shrink-0"></span>
                  <span className="text-gray-300">B — Mild</span>
                </div>
                <div className="flex items-center">
                  <span className="w-5 h-5 rounded bg-orange-200/20 border border-orange-200 mr-2 flex-shrink-0"></span>
                  <span className="text-gray-300">C — Moderate</span>
                </div>
                <div className="flex items-center">
                  <span className="w-5 h-5 rounded bg-red-400/20 border border-red-400 mr-2 flex-shrink-0"></span>
                  <span className="text-gray-300">D — Severe</span>
                </div>
                <div className="flex items-center">
                  <span className="w-5 h-5 rounded bg-red-800/30 border border-red-800 mr-2 flex-shrink-0"></span>
                  <span className="text-gray-300">E — Extensive</span>
                </div>
              </div>
            </div>
          </div>

          {/* Diagnosis Notes */}
          <div className="space-y-1">
            <Label htmlFor="diag" className="text-white text-sm font-medium">Diagnosis Notes</Label>
            <Textarea
              id="diag"
              className={`w-full rounded-lg border px-4 py-3 text-sm bg-gray-800 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                touched.diagnosisNotes && errors.diagnosisNotes ? "border-red-500" : "border-gray-600"
              }`}
              value={diagnosisNotes}
              onChange={(e) => setDiagnosisNotes(e.target.value.slice(0, MAX_NOTES))}
              onBlur={() => setFieldTouched("diagnosisNotes")}
              placeholder="Enter diagnosis details"
              rows={3}
            />
            <p className="text-xs text-gray-400">{count(diagnosisNotes)}/{MAX_NOTES}</p>
            {touched.diagnosisNotes && errors.diagnosisNotes && (
              <p className="text-xs text-red-400">{errors.diagnosisNotes}</p>
            )}
          </div>

          {/* Treatment Plan Notes */}
          <div className="space-y-1">
            <Label htmlFor="plan" className="text-white text-sm font-medium">Treatment Plan Notes</Label>
            <Textarea
              id="plan"
              className={`w-full rounded-lg border px-4 py-3 text-sm bg-gray-800 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                touched.treatmentPlanNotes && errors.treatmentPlanNotes ? "border-red-500" : "border-gray-600"
              }`}
              value={treatmentPlanNotes}
              onChange={(e) => setTreatmentPlanNotes(e.target.value.slice(0, MAX_NOTES))}
              onBlur={() => setFieldTouched("treatmentPlanNotes")}
              placeholder="Enter treatment plan details"
              rows={3}
            />
            <p className="text-xs text-gray-400">{count(treatmentPlanNotes)}/{MAX_NOTES}</p>
            {touched.treatmentPlanNotes && errors.treatmentPlanNotes && (
              <p className="text-xs text-red-400">{errors.treatmentPlanNotes}</p>
            )}
          </div>

          <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-0 sm:justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                if (onBack) onBack();
                onClose();
              }}
              className="w-full sm:w-auto bg-gray-800 border-gray-600 text-white hover:bg-gray-700"
            >
              Back
            </Button>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button
                type="button"
                variant="outline"
                onClick={handleSaveDraft}
                disabled={loading}
                className="flex-1 sm:flex-none bg-gray-800 border-gray-600 text-white hover:bg-gray-700"
              >
                {loading ? 'Saving...' : 'Save Draft'}
              </Button>
              <Button
                type="submit"
                disabled={!isFormValid || loading}
                className="flex-1 sm:flex-none medical-gradient text-white hover:opacity-90"
              >
                {loading ? 'Saving...' : 'Next'}
              </Button>
            </div>
          </DialogFooter>
        </form>

        {/* Picker Modal */}
        <Modal
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          title={selected ? `Tooth ${teethNumbers[selected.index] || ""} (${selected.jaw === "upper" ? "Upper" : "Lower"})` : "Set Grade & Status"}
        >
          {selected && (
            <PickerContent
              toothLabel={toothLabel}
              currentGrade={currentGrade}
              currentStatus={currentStatus}
              onApply={applySelection}
            />
          )}
        </Modal>
    </>
  );

  // If hideDialog is true, return only form content (for wizard)
  if (isHideDialog) {
    return formContent;
  }

  // Otherwise, return with Dialog wrapper
  return (
    <>
    <Dialog open={open && !!patientId && !showProcedureTracking} onOpenChange={onClose}>
      <DialogContent 
        className="glass-effect border-gray-700 bg-gray-900/95 backdrop-blur-sm max-w-6xl max-h-[95vh] overflow-y-auto w-[98vw] !z-[60]" 
        style={{ zIndex: 60 }}
      >
        <DialogHeader>
          <DialogTitle className="text-white text-xl sm:text-2xl">Dental Examination</DialogTitle>
          <DialogDescription className="text-gray-400">
            Record patient's chief complaint and dental findings
          </DialogDescription>
        </DialogHeader>
        {formContent}
      </DialogContent>
    </Dialog>

    {/* Procedure Tracking Form - Outside Dialog so it can overlay */}
    {!isHideDialog && (
      <ProcedureTrackingForm
        open={showProcedureTracking}
        onClose={() => {
          setShowProcedureTracking(false);
          if (onComplete) {
            onComplete(payload);
          }
        }}
        patientId={patientId}
        patientName={patientName}
        medicalHistory={medicalHistoryData}
        dentalExamination={dentalExaminationData || {
          ...payload,
          patient_type: patientType,
          chief_complaint: chiefComplaint,
          duration_onset: durationOnset,
          trigger_factors: payload.trigger_factors,
          other_trigger: otherTrigger,
          findings: payload.findings,
          diagnosis_notes: diagnosisNotes,
          treatment_plan_notes: treatmentPlanNotes
        }}
        onBack={() => {
          setShowProcedureTracking(false);
        }}
        onNext={(networkPayload, uiPayload) => {
          setShowProcedureTracking(false);
          onClose();
          if (onComplete) {
            onComplete({
              type: 'showReview',
              patientId,
              patientName,
              medicalHistory: medicalHistoryData,
              dentalExamination: dentalExaminationData || {
                ...payload,
                patient_type: patientType,
                chief_complaint: chiefComplaint,
                duration_onset: durationOnset,
                trigger_factors: payload.trigger_factors,
                other_trigger: otherTrigger,
                findings: payload.findings,
                diagnosis_notes: diagnosisNotes,
                treatment_plan_notes: treatmentPlanNotes
              },
              procedures: networkPayload
            });
          }
        }}
        onSave={(payload) => {
          console.log('Saving procedure tracking draft:', payload);
        }}
      />
    )}
    </>
  );
};

/* ========================
   Picker Content
   ======================== */
const PickerContent = ({ toothLabel, currentGrade, currentStatus, onApply }) => {
  const [g, setG] = useState(currentGrade || "");
  const [s, setS] = useState(currentStatus || "");

  useEffect(() => {
    setG(currentGrade || "");
    setS(currentStatus || "");
  }, [currentGrade, currentStatus]);

  return (
    <div className="space-y-6">
      <div className="text-sm text-gray-400 mb-4">
        Select grade and status for <span className="font-medium text-white">{toothLabel}</span>
      </div>

      {/* Grade buttons */}
      <div>
        <div className="text-sm font-medium text-white mb-3">Grade</div>
        <div className="flex flex-wrap gap-2">
          {["", ...gradeOptions].map((opt) => {
            const active = g === opt || (!g && opt === "");
            const label = opt || "None";
            const getColorClass = (grade) => {
              switch (grade) {
                case "A": return "bg-green-700/20 border-green-700 text-green-300";
                case "B": return "bg-green-200/20 border-green-200 text-green-600";
                case "C": return "bg-orange-200/20 border-orange-200 text-orange-300";
                case "D": return "bg-red-400/20 border-red-400 text-red-400";
                case "E": return "bg-red-800/30 border-red-800 text-red-300";
                default: return "bg-gray-800 border-gray-600 text-gray-300";
              }
            };
            return (
              <button
                key={opt || "clear"}
                type="button"
                onClick={() => setG(opt)}
                className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all flex items-center ${
                  opt ? getColorClass(opt) : "bg-gray-800 border-gray-600 text-gray-300"
                } ${active ? "ring-2 ring-offset-1 ring-blue-500 ring-offset-gray-900" : "hover:bg-gray-700"}`}
              >
                {active && opt && <Check className="w-4 h-4 mr-1.5" />}
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Status pills */}
      <div>
        <div className="text-sm font-medium text-white mb-3">Status</div>
        <div className="flex flex-wrap gap-2">
          {["", ...statusOptions].map((opt) => {
            const active = s === opt || (!s && opt === "");
            const label = opt || "None";
            return (
              <button
                key={opt || "clear-status"}
                type="button"
                onClick={() => setS(opt)}
                className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  active
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-gray-800 text-gray-300 border-gray-600 hover:bg-gray-700"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onApply("", "");
          }}
          className="bg-gray-800 border-gray-600 text-white hover:bg-gray-700"
        >
          Clear
        </Button>
        <Button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onApply(g, s);
          }}
          className="medical-gradient text-white hover:opacity-90"
        >
          Apply Selection
        </Button>
      </div>
    </div>
  );
};

export default DentalExaminationForm;

